import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Video,
  Upload,
  Play,
  Pause,
  Edit2,
  Trash2,
  Save,
  X,
  Plus,
  RefreshCw,
  Eye,
  Clock,
  CheckCircle,
  AlertCircle,
  Search,
  Database,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GameStorage = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingGame, setEditingGame] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newGame, setNewGame] = useState({
    event_id: 1,
    event_name: 'Gary Charles Hoops Classic',
    game_number: 1,
    title: '',
    team1_name: '',
    team1_score: 0,
    team2_name: '',
    team2_score: 0,
    game_date: '2026-01-03',
    game_time: '',
    location: 'Chaminade',
    status: 'upcoming',
    video_url: '',
    thumbnail_url: '',
    duration: '',
  });

  // Fetch games from API - combines old games, engine games with videos, and Cloudflare VODs
  const fetchGames = async () => {
    setLoading(true);
    try {
      // Fetch from multiple sources
      const [gamesRes, engineGamesRes, vodsRes] = await Promise.all([
        fetch(`${API_URL}/api/games${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`),
        fetch(`${API_URL}/api/engine/games`),
        fetch(`${API_URL}/api/vod/list`).catch(() => ({ ok: false }))
      ]);
      
      let allGames = [];
      
      // Old games
      if (gamesRes.ok) {
        const data = await gamesRes.json();
        allGames = [...data];
      }
      
      // Engine games with video URLs (convert to same format)
      // Only show video_url for final games (Cloudflare VODs gated by backend)
      if (engineGamesRes.ok) {
        const engineData = await engineGamesRes.json();
        const engineGamesWithVideo = engineData
          .filter(g => g.status === 'final')
          .map(g => ({
            id: g.game_id,
            game_id: g.game_id,
            title: g.title || `${g.home_team_name} vs ${g.away_team_name}`,
            team1_name: g.home_team_name,
            team1_score: g.home_score,
            team2_name: g.away_team_name,
            team2_score: g.away_score,
            status: 'completed',
            video_url: g.video_url,
            thumbnail_url: g.thumbnail_url,
            event_name: g.tournament_name || 'Single Game',
            game_date: g.created_at?.split('T')[0],
            source: 'engine'
          }));
        
        // Avoid duplicates by game_id
        const existingIds = new Set(allGames.map(g => g.id || g.game_id));
        engineGamesWithVideo.forEach(g => {
          if (!existingIds.has(g.game_id)) {
            allGames.push(g);
          }
        });
      }
      
      // Cloudflare VODs
      if (vodsRes.ok) {
        const vodsData = await vodsRes.json();
        if (vodsData.videos) {
          const cloudflareVods = vodsData.videos.map(v => ({
            id: v.uid,
            title: v.meta?.name || 'Untitled Video',
            status: v.status?.state === 'ready' ? 'completed' : 'processing',
            video_url: v.playback?.hls || v.preview,
            thumbnail_url: v.thumbnail,
            duration: v.duration ? `${Math.floor(v.duration / 60)}:${(v.duration % 60).toString().padStart(2, '0')}` : '',
            created_at: v.created,
            source: 'cloudflare'
          }));
          
          const existingIds = new Set(allGames.map(g => g.id || g.game_id));
          cloudflareVods.forEach(v => {
            if (!existingIds.has(v.id)) {
              allGames.push(v);
            }
          });
        }
      }
      
      // Sort by date (newest first)
      allGames.sort((a, b) => {
        const dateA = new Date(a.created_at || a.game_date || 0);
        const dateB = new Date(b.created_at || b.game_date || 0);
        return dateB - dateA;
      });
      
      setGames(allGames);
    } catch (error) {
      console.error('Error fetching games:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchGames();
  }, [statusFilter]);

  // Create new game
  const handleCreateGame = async () => {
    try {
      const response = await fetch(`${API_URL}/api/games`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newGame),
      });
      if (response.ok) {
        toast.success('Game created successfully');
        setShowAddForm(false);
        setNewGame({
          event_id: 1,
          event_name: 'Gary Charles Hoops Classic',
          game_number: 1,
          title: '',
          team1_name: '',
          team1_score: 0,
          team2_name: '',
          team2_score: 0,
          game_date: '2026-01-03',
          game_time: '',
          location: 'Chaminade',
          status: 'upcoming',
          video_url: '',
          thumbnail_url: '',
          duration: '',
        });
        fetchGames();
      } else {
        toast.error('Failed to create game');
      }
    } catch (error) {
      toast.error('Error creating game');
    }
  };

  // Update game
  const handleUpdateGame = async (gameId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/games/${gameId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (response.ok) {
        toast.success('Game updated successfully');
        setEditingGame(null);
        fetchGames();
      } else {
        toast.error('Failed to update game');
      }
    } catch (error) {
      toast.error('Error updating game');
    }
  };

  // Delete game
  const handleDeleteGame = async (gameId) => {
    if (!window.confirm('Are you sure you want to delete this game?')) return;

    const authToken = sessionStorage.getItem('rooam_token') || localStorage.getItem('rooam_token');
    if (!authToken) {
      toast.error('Please log in to delete games');
      return;
    }
    try {
      // Use the modern engine endpoint so DELETE cascade-cleans across all
      // related collections (VODs, clips, stream sessions, rosters, etc.).
      const response = await fetch(`${API_URL}/api/engine/games/${gameId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      if (response.ok) {
        toast.success('Game deleted successfully');
        fetchGames();
      } else {
        toast.error('Failed to delete game');
      }
    } catch (error) {
      toast.error('Error deleting game');
    }
  };

  // Filter games by search
  const filteredGames = games.filter((game) =>
    game.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    game.team1_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    game.team2_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'live':
        return <Badge className="bg-red-600 text-white"><Play className="h-3 w-3 mr-1" />Live</Badge>;
      case 'completed':
        return <Badge className="bg-green-600 text-white"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
      default:
        return <Badge className="bg-yellow-500 text-black"><Clock className="h-3 w-3 mr-1" />Upcoming</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-rooam-black py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Database className="h-8 w-8 text-rooam-blue" />
              Game Storage Manager
            </h1>
            <p className="text-gray-400 mt-1">Manage game recordings and replay videos</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="border-rooam-gray-light text-gray-300 hover:bg-rooam-gray-medium"
              onClick={fetchGames}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              className="bg-rooam-blue hover:bg-rooam-blue-light text-black"
              onClick={() => setShowAddForm(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Game
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
          <Card className="bg-rooam-gray-medium border-rooam-gray-light">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-rooam-blue/20 p-2 rounded-lg">
                  <Video className="h-5 w-5 text-rooam-blue" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{games.length}</p>
                  <p className="text-gray-400 text-sm">Total Games</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-rooam-gray-medium border-rooam-gray-light">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-red-500/20 p-2 rounded-lg">
                  <Play className="h-5 w-5 text-red-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {games.filter((g) => g.status === 'live').length}
                  </p>
                  <p className="text-gray-400 text-sm">Live Now</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-rooam-gray-medium border-rooam-gray-light">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-green-500/20 p-2 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {games.filter((g) => g.status === 'completed').length}
                  </p>
                  <p className="text-gray-400 text-sm">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-rooam-gray-medium border-rooam-gray-light">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-yellow-500/20 p-2 rounded-lg">
                  <Clock className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">
                    {games.filter((g) => g.status === 'upcoming').length}
                  </p>
                  <p className="text-gray-400 text-sm">Upcoming</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search games..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-rooam-gray-dark border-rooam-gray-light text-white"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'upcoming', 'live', 'completed'].map((status) => (
              <Button
                key={status}
                variant="outline"
                size="sm"
                className={`border-rooam-gray-light capitalize ${
                  statusFilter === status ? 'bg-rooam-blue text-black' : 'text-gray-400'
                }`}
                onClick={() => setStatusFilter(status)}
              >
                {status}
              </Button>
            ))}
          </div>
        </div>

        {/* Add Game Form */}
        {showAddForm && (
          <Card className="bg-rooam-gray-medium border-rooam-gray-light mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span>Add New Game</span>
                <Button variant="ghost" size="icon" onClick={() => setShowAddForm(false)}>
                  <X className="h-4 w-4 text-gray-400" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  placeholder="Game Title"
                  value={newGame.title}
                  onChange={(e) => setNewGame({ ...newGame, title: e.target.value })}
                  className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                />
                <Input
                  placeholder="Team 1 Name"
                  value={newGame.team1_name}
                  onChange={(e) => setNewGame({ ...newGame, team1_name: e.target.value })}
                  className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                />
                <Input
                  placeholder="Team 2 Name"
                  value={newGame.team2_name}
                  onChange={(e) => setNewGame({ ...newGame, team2_name: e.target.value })}
                  className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                />
                <Input
                  placeholder="Game Time (e.g., 9:30 AM)"
                  value={newGame.game_time}
                  onChange={(e) => setNewGame({ ...newGame, game_time: e.target.value })}
                  className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                />
                <Input
                  placeholder="Location"
                  value={newGame.location}
                  onChange={(e) => setNewGame({ ...newGame, location: e.target.value })}
                  className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                />
                <Input
                  placeholder="Video URL"
                  value={newGame.video_url}
                  onChange={(e) => setNewGame({ ...newGame, video_url: e.target.value })}
                  className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                />
              </div>
              <div className="flex justify-end mt-4">
                <Button className="bg-rooam-blue hover:bg-rooam-blue-light text-black" onClick={handleCreateGame}>
                  <Save className="h-4 w-4 mr-2" />
                  Create Game
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Games List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 text-rooam-blue animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Loading games...</p>
          </div>
        ) : filteredGames.length === 0 ? (
          <Card className="bg-rooam-gray-medium border-rooam-gray-light">
            <CardContent className="p-12 text-center">
              <Video className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No Games Found</h3>
              <p className="text-gray-400 mb-4">Start by adding games to the storage</p>
              <Button
                className="bg-rooam-blue hover:bg-rooam-blue-light text-black"
                onClick={() => setShowAddForm(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Game
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredGames.map((game) => (
              <Card key={game.id} className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardContent className="p-4">
                  {editingGame === game.id ? (
                    // Edit Mode
                    <EditGameForm
                      game={game}
                      onSave={(updates) => handleUpdateGame(game.id, updates)}
                      onCancel={() => setEditingGame(null)}
                    />
                  ) : (
                    // View Mode
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      {/* Thumbnail */}
                      <div className="w-full md:w-48 aspect-video bg-rooam-gray-dark rounded-lg overflow-hidden relative flex-shrink-0">
                        {game.thumbnail_url ? (
                          <img
                            src={game.thumbnail_url}
                            alt={game.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Video className="h-8 w-8 text-gray-600" />
                          </div>
                        )}
                        {game.video_url && (
                          <div className="absolute bottom-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                            Video Ready
                          </div>
                        )}
                      </div>

                      {/* Game Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getStatusBadge(game.status)}
                          <span className="text-gray-500 text-sm">Game #{game.game_number}</span>
                        </div>
                        <h3 className="text-lg font-bold text-white mb-1">{game.title}</h3>
                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span>{game.team1_name} vs {game.team2_name}</span>
                          {game.status !== 'upcoming' && (
                            <span className="text-white font-bold">
                              {game.team1_score} - {game.team2_score}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                          <span>{game.game_date} • {game.game_time}</span>
                          <span>{game.location}</span>
                          {game.views > 0 && (
                            <span className="flex items-center gap-1">
                              <Eye className="h-3 w-3" />
                              {game.views} views
                            </span>
                          )}
                        </div>
                        {game.video_url && (
                          <div className="mt-2">
                            <span className="text-xs text-gray-500 break-all">
                              Video: {game.video_url.substring(0, 50)}...
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-rooam-gray-light text-gray-300"
                          onClick={() => setEditingGame(game.id)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                          onClick={() => handleDeleteGame(game.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Edit Game Form Component
const EditGameForm = ({ game, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    status: game.status,
    team1_score: game.team1_score,
    team2_score: game.team2_score,
    video_url: game.video_url || '',
    thumbnail_url: game.thumbnail_url || '',
    duration: game.duration || '',
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="text-gray-400 text-sm mb-1 block">Status</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="w-full bg-gray-900 border border-gray-700 text-white rounded-md px-3 py-2 appearance-none cursor-pointer [&>option]:bg-gray-900 [&>option]:text-white"
            style={{ colorScheme: 'dark' }}
          >
            <option value="upcoming" className="bg-gray-900 text-white">Upcoming</option>
            <option value="live" className="bg-gray-900 text-white">Live</option>
            <option value="completed" className="bg-gray-900 text-white">Completed</option>
          </select>
        </div>
        <div>
          <label className="text-gray-400 text-sm mb-1 block">Team 1 Score</label>
          <Input
            type="number"
            value={formData.team1_score}
            onChange={(e) => setFormData({ ...formData, team1_score: parseInt(e.target.value) || 0 })}
            className="bg-rooam-gray-dark border-rooam-gray-light text-white"
          />
        </div>
        <div>
          <label className="text-gray-400 text-sm mb-1 block">Team 2 Score</label>
          <Input
            type="number"
            value={formData.team2_score}
            onChange={(e) => setFormData({ ...formData, team2_score: parseInt(e.target.value) || 0 })}
            className="bg-rooam-gray-dark border-rooam-gray-light text-white"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-gray-400 text-sm mb-1 block">Video URL (Replay)</label>
          <Input
            placeholder="https://..."
            value={formData.video_url}
            onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
            className="bg-rooam-gray-dark border-rooam-gray-light text-white"
          />
        </div>
        <div>
          <label className="text-gray-400 text-sm mb-1 block">Duration</label>
          <Input
            placeholder="1:30:00"
            value={formData.duration}
            onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
            className="bg-rooam-gray-dark border-rooam-gray-light text-white"
          />
        </div>
        <div className="md:col-span-3">
          <label className="text-gray-400 text-sm mb-1 block">Thumbnail URL</label>
          <Input
            placeholder="https://..."
            value={formData.thumbnail_url}
            onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
            className="bg-rooam-gray-dark border-rooam-gray-light text-white"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" className="border-rooam-gray-light text-gray-300" onClick={onCancel}>
          Cancel
        </Button>
        <Button className="bg-rooam-blue hover:bg-rooam-blue-light text-black" onClick={() => onSave(formData)}>
          <Save className="h-4 w-4 mr-2" />
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default GameStorage;
